import { useMemo } from "react";
import { startOfDay } from "date-fns/startOfDay";
import { subDays } from "date-fns/subDays";
import { BarChart, Bar, XAxis, ResponsiveContainer } from "recharts";
import type { EnduserObservation } from "@tellescope/types-models";
import { MACRO_COLORS, sumMacro } from "./metrics";

interface WeekMacroBarChartProps {
  foodEntries: EnduserObservation[];
}

interface WeekDayTotals {
  dayLabel: string;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
}

// 7-day stacked macro bar chart, matching the prototype's WeekChart: last 7 calendar days,
// oldest to newest, protein/carbs/fat/fiber stacked in that order.
export const WeekMacroBarChart = ({ foodEntries }: WeekMacroBarChartProps) => {
  const days = useMemo<WeekDayTotals[]>(() => {
    const today = startOfDay(new Date());
    return Array.from({ length: 7 }, (_, i) => {
      const day = subDays(today, 6 - i);
      const entriesForDay = foodEntries.filter((o) => startOfDay(new Date(o.timestamp)).getTime() === day.getTime());
      return {
        dayLabel: day.toLocaleDateString(undefined, { weekday: "narrow" }),
        protein: sumMacro(entriesForDay, "Protein"),
        carbs: sumMacro(entriesForDay, "Carbohydrate"),
        fat: sumMacro(entriesForDay, "Fat"),
        fiber: sumMacro(entriesForDay, "Fiber"),
      };
    });
  }, [foodEntries]);

  return (
    <div className="h-20">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={days} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} barGap={2}>
          <XAxis dataKey="dayLabel" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
          <Bar dataKey="protein" stackId="macros" fill={MACRO_COLORS.protein} />
          <Bar dataKey="carbs" stackId="macros" fill={MACRO_COLORS.carbs} />
          <Bar dataKey="fat" stackId="macros" fill={MACRO_COLORS.fat} />
          <Bar dataKey="fiber" stackId="macros" fill={MACRO_COLORS.fiber} radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
